import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Shared form primitives. All three Settings sub-pages compose from these
 * so the visual language stays identical (focus ring, border, sizing).
 */

const INPUT_BASE =
  "block w-full rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none placeholder:text-text-faint focus:border-coral disabled:opacity-50 disabled:cursor-not-allowed";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(INPUT_BASE, className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(INPUT_BASE, "resize-y leading-relaxed", className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          INPUT_BASE,
          // Native select arrow on Windows is fine; we only restyle the box.
          "pr-8 appearance-auto",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

// ---------------------------------------------------------------------------
// Slider — number with a range input + an editable numeric input side by
// side. Used for the bot's temperature / top_k / threshold controls.
// ---------------------------------------------------------------------------

interface SliderProps {
  id?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
}

export function Slider({ id, value, onChange, min, max, step = 1, unit, disabled }: SliderProps) {
  return (
    <div className="flex items-center gap-3">
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className={cn(
          "h-1 flex-1 cursor-pointer appearance-none rounded-full bg-line",
          // Webkit thumb
          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4",
          "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-coral [&::-webkit-slider-thumb]:cursor-pointer",
          "[&::-webkit-slider-thumb]:border-0",
          // Firefox thumb
          "[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full",
          "[&::-moz-range-thumb]:bg-coral [&::-moz-range-thumb]:border-0",
        )}
      />
      <div className="flex w-20 items-center gap-1">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full rounded border border-line bg-surface px-2 py-1 text-right text-xs tabular-nums text-text-strong outline-none focus:border-coral"
        />
        {unit && <span className="text-xs text-text-soft">{unit}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SegmentedControl — radio-group-style chooser for small option sets
// (preset selectors, position, initial state).
// ---------------------------------------------------------------------------

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; hint?: string }>;
  size?: "sm" | "md";
}

export function SegmentedControl<T extends string>({ value, onChange, options, size = "md" }: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" className="inline-flex flex-wrap gap-1 rounded border border-line bg-surface-2 p-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            title={opt.hint}
            className={cn(
              "rounded transition-colors",
              size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
              active
                ? "bg-surface text-text-strong"
                : "text-text-soft hover:text-text-strong",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
